import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config";

import {
  FaMapMarkerAlt,
  FaDollarSign,
  FaTruck,
  FaToolbox,
  FaCar,
  FaCog,
  FaInfoCircle,
  FaArrowLeft,
  FaClipboardList,
  FaAddressCard,
  FaCheckCircle,
} from "react-icons/fa";

export default function BusinessDetails() {
  const { id } = useParams();
  const navigate = useNavigate();


  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showEnquiry, setShowEnquiry] = useState(false);

  useEffect(() => {
    async function loadListing() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/lawn-mowing/business/${id}`);

        if (res.data.success) {
          setListing(res.data.listing);
        }
      } catch (err) {
        console.error("Failed loading business details:", err);
      } finally {
        setLoading(false);
      }
    }

    loadListing();
  }, [id, API_BASE_URL]);

  if (loading)
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</p>;
  if (!listing)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem" }}>
        Business not found.
      </p>
    );

  /* ------------------------------
      UI HELPERS
  ------------------------------- */
  const Section = ({ title, icon, children }) => (
    <section style={{ marginTop: "25px" }}>
      <h2
        style={{
          borderBottom: "2px solid #2e7d32",
          paddingBottom: "5px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: "#2e7d32",
          fontSize: "1.3rem",
        }}
      >
        {icon} {title}
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "15px",
          marginTop: "10px",
        }}
      >
        {children}
      </div>
    </section>
  );

  const Info = ({ icon, label, value }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#f9f9f9",
        borderRadius: "8px",
        padding: "10px 12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        gap: "12px",
      }}
    >
      <div style={{ fontSize: "1.3rem", color: "#2e7d32" }}>{icon}</div>
      <div>
        <div style={{ fontWeight: "600", color: "#333" }}>{label}</div>
        <div style={{ color: "#2e7d32" }}>{value}</div>
      </div>
    </div>
  );

  /* ------------------------------
      MAIN UI
  ------------------------------- */
  return (
    <div
      style={{
        maxWidth: "950px",
        margin: "2rem auto",
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      {/* HERO IMAGE */}
      <div style={{ position: "relative" }}>
        <img
          src={"/images/Grass.jpg"}
          alt="Business"
          style={{ width: "100%", height: "380px", objectFit: "cover" }}
        />

        <button
          onClick={() => navigate("/lawn-mowing/business")}
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            background: "rgba(255,255,255,0.9)",
            border: "none",
            borderRadius: "50%",
            padding: "10px",
            cursor: "pointer",
          }}
        >
          <FaArrowLeft />
        </button>
      </div>

      {/* CONTENT */}
      <div style={{ padding: "25px" }}>
        {/* Badge */}
        {listing.enquiry_only === 1 && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              padding: "10px 15px",
              borderRadius: "6px",
              marginBottom: "15px",
              fontWeight: "600",
            }}
          >
            Enquiry-Only Listing
          </div>
        )}

        <h1 style={{ color: "#2e7d32", marginBottom: "5px" }}>
          {listing.business_title}
        </h1>

        <p style={{ color: "#999", margin: "3px 0" }}>
          Business ID: BUS-{listing.id}
        </p>

        <p style={{ color: "#666", marginBottom: "10px" }}>
          <FaMapMarkerAlt
            style={{ color: "#2e7d32", marginRight: "5px" }}
          />
          {listing.suburb}, {listing.state} {listing.postcode}
        </p>

        <h2 style={{ marginTop: "10px", color: "#2e7d32" }}>
          Price: ${Number(listing.price).toLocaleString()}
        </h2>

        {/* BUSINESS OVERVIEW */}
        <Section title="Business Overview" icon={<FaClipboardList />}>
          <Info
            icon={<FaDollarSign />}
            label="Total Cut Value"
            value={`$${Number(listing.total_cut_value).toLocaleString()}`}
          />
          <Info
            icon={<FaDollarSign />}
            label="Yearly Revenue"
            value={`$${Number(listing.yearly_revenue).toLocaleString()}`}
          />
          <Info
            icon={<FaAddressCard />}
            label="Clients"
            value={listing.clients_count || "N/A"}
          />
      
        </Section>

        <section style={{ marginTop: "25px" }}>
  <h2
    style={{
      borderBottom: "2px solid #2e7d32",
      paddingBottom: "5px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "#2e7d32",
      fontSize: "1.3rem",
    }}
  >
    <FaInfoCircle /> Description
  </h2>

  <div
    style={{
      background: "#f9f9f9",
      borderRadius: "8px",
      padding: "20px",
      lineHeight: "1.7",
      color: "#333",
      whiteSpace: "pre-wrap",
      marginTop: "10px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
  >
    {listing.business_description}
  </div>
</section>

        {/* INCLUDED ITEMS */}
        <Section title="Included Items" icon={<FaToolbox />}>
          <Info
            icon={<FaCar />}
            label="Vehicle Included"
            value={listing.vehicle_included ? "Yes" : "No"}
          />
          <Info
            icon={<FaTruck />}
            label="Trailer"
            value={listing.trailer_included ? "Yes" : "No"}
          />
          <Info
            icon={<FaCog />}
            label="Equipment"
            value={listing.equipment_included ? "Yes" : "No"}
          />
          {listing.vehicle_details && (
            <Info
              icon={<FaCar />}
              label="Vehicle Details"
              value={listing.vehicle_details}
            />
          )}
          {listing.trailer_details && (
            <Info
              icon={<FaTruck />}
              label="Trailer Details"
              value={listing.trailer_details}
            />
          )}
          {listing.equipment_details && (
            <Info
              icon={<FaCog />}
              label="Equipment Details"
              value={listing.equipment_details}
            />
          )}
        </Section>

        {/* LISTING VERIFICATION */}
<section style={{ marginTop: "30px" }}>
  <h2
    style={{
      borderBottom: "2px solid #2e7d32",
      paddingBottom: "5px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "#2e7d32",
      fontSize: "1.3rem",
    }}
  >
    <FaCheckCircle /> Listing Verification
  </h2>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "15px",
      marginTop: "10px",
    }}
  >
    <Info
      icon={<FaCheckCircle />}
      label="Posted On"
      value={
        listing.created_at
          ? new Date(listing.created_at).toLocaleDateString("en-AU", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "Loading..."
      }
    />

    <Info
      icon={<FaCheckCircle />}
      label="Disclaimer"
      value="Seller confirms this listing is genuine."
    />
  </div>
</section>

        {/* CONTACT SELLER BUTTON */}
        <div style={{ textAlign: "center", marginTop: "40px" }}>
          <button
          
  onClick={() => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (user && user.id === listing.user_id) {
      alert("You cannot enquire on your own business listing.");
      return;
    }

    setShowEnquiry(true);
  }}

            style={{
              padding: "14px 22px",
              background: "#2e7d32",
              color: "#fff",
              border: "none",
              fontSize: "1.2rem",
              fontWeight: "bold",
              borderRadius: "8px",
              cursor: "pointer",
              width: "100%",
              maxWidth: "300px",
            }}
          >
            Contact Seller
          </button>
        </div>
      </div>

      {/* ENQUIRY POPUP */}
      {showEnquiry && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "25px",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "500px",
            }}
          >
            <h2 style={{ marginTop: 0, color: "#2e7d32" }}>
              Contact Seller
            </h2>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const token = localStorage.getItem("token");

                const payload = {
                  listing_id: listing.id,
                  listing_type: "business",
                  message: e.target.message.value,
                };

                try {
                  const res = await axios.post(
                    `${API_BASE_URL}/api/contact-seller`,
                    payload,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if (res.data.success) {
                    alert("Your enquiry has been sent.");
                    setShowEnquiry(false);
                  } else {
                    alert("Failed to send enquiry.");
                  }
                } catch (err) {
                  console.error("Enquiry Error:", err);
                  alert(
  "Contacting sellers is available to verified Client Exchange members only. Please log in or create a free account to continue. Identity verification is required before you can contact sellers."
);
                }
              }}
            >
              <textarea
                name="message"
                placeholder="Write your message to the seller..."
                rows="4"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  marginBottom: "15px",
                }}
                required
              />

              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Send Enquiry
              </button>

              <button
                type="button"
                onClick={() => setShowEnquiry(false)}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginTop: "10px",
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
